import { useState, useRef, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Line, Cell, PieChart, Pie } from "recharts";
import { DashboardCard, MetricCard } from "../../../component/new-ui/cards";
import { LineChartIcon, LoanBalance, SalarySnap, TeamAvailability } from "../../../component/icons/user-dashboard";
import { fmt, money, pkr, usd } from "../../../component/custom-hooks/capitalizeWords";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import moment from "moment";

const LOC_COLORS = { Ittehad: "#4f6ef7", "Phase 8": "#7c5ce0", "Phase 8 II": "#14b8a6", Remote: "#f97316" };
const CAT_COLORS = { Salaries: "#4f6ef7", "Rent All": "#7c5ce0", Electricity: "#f59e0b", Internet: "#14b8a6", Generator: "#f97316", Maintenance: "#ef4444", Security: "#8b5cf6", Groceries: "#06b6d4", "Bank Charges": "#9ca3af", Repairs: "#e11d48", "Advance Salary": "#84cc16", Miscellaneous: "#6b7280" };
const CAT_ICONS = { Salaries: "👥", "Rent All": "🏠", Electricity: "⚡", Internet: "📡", Generator: "🔋", Maintenance: "🔧", Security: "🔒", Groceries: "💧", "Bank Charges": "🏦", Repairs: "🛠", "Advance Salary": "💰", Miscellaneous: "💳" };
const LOC_EMOJI = { Ittehad: "🏢", "Phase 8": "🏢", "Phase 8 II": "🏗", Remote: "🌐" };
const ALL_MONTHS = ["Apr 2025", "May 2025", "Jun 2025", "Jul 2025", "Aug 2025", "Sep 2025", "Oct 2025", "Nov 2025", "Dec 2025", "Jan 2026", "Feb 2026", "Mar 2026"];
const RANGE_COUNTS = { "3M": 3, "6M": 6, "12M": 12 };
const MONS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


// ─── DATA ─────────────────────────────────────────────────────────────────────
const MONTHLY_DATA = {
  "Jan 2026": { openBal: 0, closeBal: 655, remittances: [{ usd: 12000, fx: 278.0, gross: 3336000, wht: 8340, net: 3327660 }, { usd: 9000, fx: 278.5, gross: 2506500, wht: 6266, net: 2500234 }], expenses: { Salaries: 4050000, Rent: 449450, Electricity: 328000, Internet: 126760, Generator: 35000, Maintenance: 46500 }, expByLoc: { Ittehad: 2200000, "Phase 8": 1100000, Remote: 735710, "Phase 8 II": 0 }, overdue: 0 },
  "Feb 2026": { openBal: 655, closeBal: 6686, remittances: [{ usd: 15500, fx: 279.75, gross: 4336125, wht: 10840, net: 4325285 }, { usd: 3600, fx: 279.20, gross: 1005120, wht: 2513, net: 1002607 }], expenses: { Salaries: 4433449, Rent: 449450, Electricity: 301396, Internet: 126760, Generator: 35000, Maintenance: 46500, "Advance Salary": 15000, "Bank Charges": 2760 }, expByLoc: { Ittehad: 2800000, "Phase 8": 1200000, Remote: 350000, "Phase 8 II": 0 }, overdue: 242890 },
  "Mar 2026": { openBal: 6686, closeBal: null, remittances: [], expenses: { Rent: 514450, Electricity: 420000, Internet: 126760, Generator: 90000, Maintenance: 55000, Security: 65000, Groceries: 6840 }, expByLoc: { Ittehad: 800000, "Phase 8": 350000, Remote: 250000, "Phase 8 II": 141000 }, overdue: 242890 },
  "Dec 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 19000, fx: 277.5, gross: 5272500, wht: 13181, net: 5259319 }], expenses: { Salaries: 3980000, Rent: 449450, Electricity: 412000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2600000, "Phase 8": 1200000, Remote: 400000, "Phase 8 II": 0 }, overdue: 0 },
  "Nov 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 17500, fx: 276.8, gross: 4844000, wht: 12110, net: 4831890 }], expenses: { Salaries: 3850000, Rent: 449450, Electricity: 395000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200, Repairs: 45000 }, expByLoc: { Ittehad: 2400000, "Phase 8": 1100000, Remote: 420000, "Phase 8 II": 0 }, overdue: 0 },
  "Oct 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 20000, fx: 275.0, gross: 5500000, wht: 13750, net: 5486250 }], expenses: { Salaries: 3920000, Rent: 449450, Electricity: 380000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2500000, "Phase 8": 1150000, Remote: 410000, "Phase 8 II": 0 }, overdue: 0 },
  "Sep 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 18000, fx: 274.5, gross: 4941000, wht: 12352, net: 4928648 }], expenses: { Salaries: 3800000, Rent: 449450, Electricity: 310000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2350000, "Phase 8": 1050000, Remote: 390000, "Phase 8 II": 0 }, overdue: 0 },
  "Aug 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 16500, fx: 273.0, gross: 4504500, wht: 11261, net: 4493239 }], expenses: { Salaries: 3750000, Rent: 449450, Electricity: 290000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2300000, "Phase 8": 1000000, Remote: 380000, "Phase 8 II": 0 }, overdue: 0 },
  "Jul 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 17000, fx: 272.0, gross: 4624000, wht: 11560, net: 4612440 }], expenses: { Salaries: 3700000, Rent: 449450, Electricity: 275000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200, Repairs: 120000 }, expByLoc: { Ittehad: 2250000, "Phase 8": 1000000, Remote: 370000, "Phase 8 II": 0 }, overdue: 0 },
  "Jun 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 15000, fx: 270.0, gross: 4050000, wht: 10125, net: 4039875 }], expenses: { Salaries: 3650000, Rent: 449450, Electricity: 260000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2200000, "Phase 8": 980000, Remote: 360000, "Phase 8 II": 0 }, overdue: 0 },
  "May 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 16000, fx: 269.5, gross: 4312000, wht: 10780, net: 4301220 }], expenses: { Salaries: 3600000, Rent: 449450, Electricity: 245000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2150000, "Phase 8": 960000, Remote: 350000, "Phase 8 II": 0 }, overdue: 0 },
  "Apr 2025": { openBal: 0, closeBal: 0, remittances: [{ usd: 14500, fx: 268.0, gross: 3886000, wht: 9715, net: 3876285 }], expenses: { Salaries: 3550000, Rent: 449450, Electricity: 230000, Internet: 126760, Generator: 70000, Maintenance: 46500, Security: 64000, Groceries: 13680, "Bank Charges": 2200 }, expByLoc: { Ittehad: 2100000, "Phase 8": 940000, Remote: 340000, "Phase 8 II": 0 }, overdue: 0 },
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800;900&family=Inter:wght@400;500;600&display=swap');
  * { box-sizing:border-box; margin:0; padding:0; }
  .nav-btn:hover { color:rgba(255,255,255,0.8) !important; }
  .kpi:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(0,0,0,0.11) !important; }
  .mon-cell { transition:all 0.1s; }
  .mon-cell:hover:not(:disabled) { background:#eef1ff !important; color:#4f6ef7 !important; }
  .mon-cell.active { background:#4f6ef7 !important; color:#fff !important; border-color:#4f6ef7 !important; }
  .mon-cell:disabled { opacity:0.3; cursor:default; }
  .range-btn { transition:all 0.1s; }
  .range-btn:hover { background:#f0f4ff !important; color:#4f6ef7 !important; }
  .range-btn.on { background:#4f6ef7 !important; color:#fff !important; }
  .cat-row:hover { background:#f7f8ff !important; }
  .loc-card:hover { transform:translateY(-1px); box-shadow:0 4px 14px rgba(0,0,0,0.09) !important; }
  ::-webkit-scrollbar { width:4px; height:4px; }
  ::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:4px; }
`;

// ─── TOOLTIPS ─────────────────────────────────────────────────────────────────
const DarkTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1e2128", borderRadius: 10, padding: "11px 14px", boxShadow: "0 8px 28px rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.07)", minWidth: 170 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: 2, background: p.color, flexShrink: 0 }} />{p.name}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "'Outfit',sans-serif" }}>{money(p.value, { compact: true })}</span>
        </div>
      ))}
    </div>
  );
};

const PieTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div style={{ background: "#1e2128", borderRadius: 10, padding: "10px 14px", boxShadow: "0 8px 28px rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 3 }}>{p.name}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", fontFamily: "'Outfit',sans-serif" }}>{money(p.value, { compact: true })}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: p.payload.fill, marginTop: 2 }}>{p.payload.pct}%</div>
    </div>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Reports() {

  const apiCall = useApiCall()

  const [snapMonth, setSnapMonth] = useState(moment(new Date()).format("MMM YYYY"));
  const [trendRange, setTrendRange] = useState("6M");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(2026);
  const [hovCat, setHovCat] = useState(null);
  const pickerRef = useRef(null);

  const [isLoading, setIssLoading] = useState(true)
  const [apiData, setApiData] = useState({})
  const [allMonths, setAllMonths] = useState([]);
  const [allTrendData, setAllTrendData] = useState([])

  const getAvailMonth = async() => {
    try {
      const apiRes = await apiCall({method: "get", url: "cash-flow/reports/available-months"});
      setAllMonths(apiRes?.data?.flat)
    } catch (error) {
      console.log("Err", error)
    }
  }

  const getData = async() => {
    try {
      const apiRes = await apiCall({method: "get", url: "cash-flow/reports/snapshot"})
      // console.log("apiRes", apiRes)
      setApiData(apiRes?.data);
      setIssLoading(false)
    } catch (error) {
      console.log("Err", error)
    }
  }

  const getTrendData = async() => {
    try {
      const apiRes = await apiCall({method: "get", url: "cash-flow/reports/trends"})
      setAllTrendData(apiRes?.data?.chart_data)
    } catch (error) {
      console.log("Err", error)
    }
  }

  useEffect(() => {
    getData();
    getAvailMonth();
    getTrendData()
  } , [])

  // Close picker on outside click
  useEffect(() => {
    const h = e => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const C = {
    navBg: "#1e2128", pageBg: "#EDE8E1", card: "#ffffff",
    accent: "#4f6ef7", green: "#16a34a", red: "#dc2626", amber: "#d97706", purple: "#7c5ce0",
    textPri: "#111827", textSec: "#4b5563", textMut: "#9ca3af",
    border: "rgba(0,0,0,0.07)", shadow: "0 1px 3px rgba(0,0,0,0.04),0 4px 14px rgba(0,0,0,0.07)",
  };

  // ── Computed values ──
  const d = MONTHLY_DATA[snapMonth];
  const totalRemNet = d ? d.remittances.reduce((s, r) => s + r.net, 0) : 0;
  const totalRemUSD = d ? d.remittances.reduce((s, r) => s + r.usd, 0) : 0;
  const totalWHT = d ? d.remittances.reduce((s, r) => s + r.wht, 0) : 0;
  const totalExp = d ? Object.values(d.expenses).reduce((s, v) => s + v, 0) : 0;
  const netFlow = totalRemNet - totalExp;
  const closingBal = d ? (d.closeBal ?? (d.openBal + netFlow)) : 0;

  // const catData = d ? Object.entries(d.expenses)
  //   .filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])
  //   .map(([cat, val]) => ({ name: cat, value: val, fill: CAT_COLORS[cat] || "#6b7280", pct: totalExp > 0 ? ((val / totalExp) * 100).toFixed(1) : "0" }))
  //   : [];

  const catData = apiData?.by_category ? apiData?.by_category?.map((eachCat, i) => ({ name: eachCat?.name, value: Number(eachCat?.total?.replaceAll(",", "")), fill: CAT_COLORS[eachCat?.name] || "#6b7280", pct: eachCat?.pct })) : []

  // const locData = d ? Object.entries(d.expByLoc)
  //   .filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])
  //   .map(([loc, val]) => ({ name: loc, value: val, fill: LOC_COLORS[loc] || C.accent, pct: totalExp > 0 ? ((val / totalExp) * 100).toFixed(1) : "0" }))
  //   : [];

  const locData = apiData?.by_location ? apiData?.by_location?.map((eachLocation, i) => ({ name: eachLocation?.name, value: Number(eachLocation?.total?.replaceAll(",", "")), fill: LOC_COLORS[eachLocation?.name] || C.accent, pct: eachLocation?.pct })) : []

  const trendMonths = allTrendData.slice(-RANGE_COUNTS[trendRange]);
  // const trendData = trendMonths.map(m => {
  //   const md = MONTHLY_DATA[m];
  //   const inn = md ? md.remittances.reduce((s, r) => s + r.net, 0) : 0;
  //   const out = md ? Object.values(md.expenses).reduce((s, v) => s + v, 0) : 0;
  //   return { month: m.split(" ")[0], In: inn, Out: out, Net: inn - out };
  // });

  const trendData = allTrendData.slice(-RANGE_COUNTS[trendRange]);

  const availSet = new Set(allMonths);

  const remittances = apiData?.pnl?.remittances || [];

  const totalExpensesCat = apiData?.by_category ? apiData?.by_category?.reduce((s, b) => s + Number(b.total?.replaceAll(",", "")), 0) : 0;

  return (
    <>
      <style>{css}</style>
      <div className="overflow-auto flex flex-col">
        <div style={{ flex: 1, padding: "24px 0px", width: "100%", margin: "0 auto" }}>
          {/* ── PAGE Header ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: C.textPri, letterSpacing: -0.5, margin: 0 }}>Reports</h1>
              <p style={{ fontSize: 13, color: C.textSec, marginTop: 3 }}>Full ledger · every transaction · running PKR balance</p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {/* Calendar month picker trigger */}
              <div ref={pickerRef} style={{ position: "relative" }}>
                <button
                  onClick={() => setPickerOpen(o => !o)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 14px", borderRadius: 10,
                    border: `1.5px solid ${pickerOpen ? C.accent : C.border}`,
                    background: pickerOpen ? "#eef1ff" : "#f9f9f8",
                    cursor: "pointer", fontSize: 13, fontWeight: 600,
                    color: pickerOpen ? C.accent : C.textPri,
                    transition: "all 0.12s",
                  }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {snapMonth}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ transform: pickerOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {/* Dropdown calendar */}
                {pickerOpen && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 300,
                    background: "#fff", borderRadius: 14, border: `1px solid ${C.border}`,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.14)", padding: 0, minWidth: 260, overflow: "hidden",
                  }}>
                    {/* Year nav */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: "#f8f8f7", borderBottom: `1px solid ${C.border}` }}>
                      <button onClick={() => setPickerYear(y => Math.max(2025, y - 1))} disabled={pickerYear <= 2025}
                        style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${C.border}`, background: "#fff", cursor: pickerYear <= 2025 ? "not-allowed" : "pointer", fontSize: 13, color: pickerYear <= 2025 ? "#d1d5db" : C.textPri, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>‹</button>
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.textPri, fontFamily: "'Outfit',sans-serif" }}>{pickerYear}</span>
                      <button onClick={() => setPickerYear(y => Math.min(2026, y + 1))} disabled={pickerYear >= 2026}
                        style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${C.border}`, background: "#fff", cursor: pickerYear >= 2026 ? "not-allowed" : "pointer", fontSize: 13, color: pickerYear >= 2026 ? "#d1d5db" : C.textPri, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>›</button>
                    </div>
                    {/* Month grid 4×3 */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 5, padding: "10px" }}>
                      {MONS_SHORT.map(mon => {
                        const key = `${mon} ${pickerYear}`;
                        const avail = availSet.has(key);
                        const on = snapMonth === key;
                        return (
                          <button key={mon} className={`mon-cell${on ? " active" : ""}`}
                            onClick={() => { if (avail) { setSnapMonth(key); setPickerOpen(false); } }}
                            disabled={!avail}
                            style={{ padding: "8px 0", borderRadius: 8, border: `1px solid ${on ? C.accent : C.border}`, cursor: avail ? "pointer" : "default", fontSize: 12, fontWeight: 600, background: on ? C.accent : "#fff", color: on ? "#fff" : avail ? C.textPri : "#d1d5db", textAlign: "center" }}>
                            {mon}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Export buttons */}
              <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: "#fff", fontSize: 12, fontWeight: 600, color: C.textSec, cursor: "pointer" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                PDF
              </button>
              <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: "#fff", fontSize: 12, fontWeight: 600, color: C.textSec, cursor: "pointer" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Excel
              </button>
            </div>
          </div>

          {/* ── PAGE CONTENT ── */}
          <div style={{ flex: 1 }}>

            {/* ── 5 KPI CARDS ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 18 }}>
              <MetricCard
                icon={<span>💵</span>}
                iconColor={C.green}
                iconBg={"#dcfce7"}
                valueColor={C.green}
                value={pkr(apiData?.kpi2?.remittances_in?.pkr)}
                statsBadge={apiData?.kpi2?.remittances_in?.sub}
                label={"Remittances In"}
              />
              <MetricCard
                icon={<span>📤</span>}
                iconColor={C.red}
                iconBg={"#fee2e2"}
                valueColor={C.red}
                value={pkr(apiData?.kpi2?.expenses_out?.pkr)}
                statsBadge={apiData?.kpi2?.expenses_out?.sub}
                label={"Total Expenses"}
              />
              <MetricCard
                icon={<span>{apiData?.kpi2?.net_flow?.pkr >= 0 ? "📈" : "📉"}</span>}
                iconColor={apiData?.kpi2?.net_flow?.pkr >= 0 ? "#16a34a" : C.red}
                iconBg={apiData?.kpi2?.net_flow?.pkr >= 0 ? "#dcfce7" : "#fee2e2"}
                valueColor={apiData?.kpi2?.net_flow?.pkr >= 0 ? "#16a34a" : C.red}
                value={pkr(apiData?.kpi2?.net_flow?.pkr)}
                statsBadge={apiData?.kpi2?.net_flow?.pkr >= 0 ? "Surplus this month" : "Deficit this month"}
                label={"Net Cash Flow"}
              />
              <MetricCard
                icon={<span>🏦</span>}
                iconColor={C.accent}
                iconBg={"#e0e7ff"}
                valueColor={C.accent}
                value={pkr(apiData?.kpi2?.petty_cash?.pkr)}
                statsBadge={apiData?.kpi2?.petty_cash?.sub}
                label={"Closing Balance"}
              />
              <MetricCard
                icon={<span>🧾</span>}
                iconColor={C.amber}
                iconBg={"#fef3c7"}
                valueColor={C.amber}
                value={pkr(apiData?.kpi2?.wht_deducted?.pkr)}
                statsBadge={apiData?.kpi2?.wht_deducted?.sub}
                label={"WHT Deducted"}
              />
            </div>

            {/* ── MAIN 3-COLUMN ── */}
            <div className="grid grid-cols-12 gap-4 mb-4">

              {/* ── P&L Breakdown ── */}
              <DashboardCard
                Icon={<TeamAvailability />}
                iconColor={'bg-theme-green-light text-theme-green'}
                heading="P&L Breakdown"
                subHeading={snapMonth}
                DataNotFoundText={`P&L Breakdown`}
                footer={null}
                loading={isLoading}
                condition={true}
                moreOff={false}
                className={'col-span-3'}
              >
                <div className="px-7 pt-5">
                  <div>
                    {[
                      { label: "Opening Balance", val: pkr(apiData?.pnl?.opening_balance || 0), color: C.accent, bold: true },
                      { label: "Remittances In", val: pkr(apiData?.pnl?.total_remittances_net), color: C.green, bold: true },
                      // ...(d?.remittances || []).map((r, i) => ({ label: `↳ Transfer ${i + 1} · ₨${r.fx}`, val: pkr(r.net), color: C.green, indent: true })),
                      // ...(totalWHT > 0 ? [{ label: "↳ WHT (0.25%)", val: (<>-{pkr(totalWHT)}</>), color: C.amber, indent: true }] : []),
                      ...(remittances?.map((r, i) => ({ label: `↳ Transfer ${i + 1} · ₨${r.fx} (${moment(r.date).format("DD MMM YYYY")})`, val: <div className="flex flex-col items-end"><div className="">{pkr(r.gross)}</div><div className="">{usd(r.usd)}</div></div>, color: C.green, indent: true }))),
                      { divider: true },
                      { label: "Total Expenses", val: pkr(apiData?.pnl?.total_expenses), color: C.red, bold: true },
                      { divider: true },
                      { label: "Net Cash Flow", val: <>{!apiData?.pnl?.net_flow.includes("-") && "+"} {pkr(apiData?.pnl?.net_flow)}</>, color: !apiData?.pnl?.net_flow.includes("-") ? C.green : C.red, bold: true },
                      { label: "Closing Balance", val: pkr(apiData?.pnl?.opening_balance), color: C.accent, bold: true, highlight: true },
                    ].map((row, i) => row.divider
                      ? <div key={i} style={{ height: 1, background: C.border, margin: "5px 0px" }} />
                      : <div key={i} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: row.highlight ? "9px 9px" : "6px 0px",
                        background: row.highlight ? "#eef1ff" : "transparent",
                        margin: row.highlight ? "4px 0px" : 0,
                        borderRadius: row.highlight ? 9 : 0,
                      }}>
                        <span style={{ fontSize: row.indent ? 10 : 12, color: row.indent ? C.textMut : C.textSec, fontWeight: row.bold ? 600 : 400, paddingLeft: row.indent ? 10 : 0 }}>{row.label}</span>
                        <span style={{ fontSize: row.bold ? 13 : 11, fontWeight: row.bold ? 800 : 500, color: row.color, fontFamily: "'Outfit',sans-serif" }}>{row.val}</span>
                      </div>
                    )}
                  </div>
                  {(apiData?.pnl?.overdue ? Number(apiData?.pnl?.overdue) : 0) > 0 && (
                    <div style={{ margin: "4px 0 12px", padding: "9px 12px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fecaca", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: C.red }}>⚠ Overdue</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.red, fontFamily: "'Outfit',sans-serif" }}>{pkr(d.overdue)}</span>
                    </div>
                  )}
                </div>
              </DashboardCard>

              <DashboardCard
                Icon={<SalarySnap />}
                iconColor={'bg-theme-purple-light text-theme-purple'}
                heading="Expenses by Category"
                subHeading={<>Total: {pkr(totalExpensesCat)}</>}
                DataNotFoundText={`Expenses by Category`}
                footer={null}
                loading={isLoading}
                condition={apiData?.by_category?.length > 1 || apiData?.by_category?.length}
                moreOff={false}
                className={'col-span-5'}
              >
                <div className="px-7 flex gap-4 items-start pt-5">
                  {/* Donut */}
                  <div className="!hidden" style={{ flexShrink: 0, position: "relative" }}>
                    <PieChart width={150} height={150}>
                      <Pie data={catData} cx={70} cy={70} innerRadius={42} outerRadius={68} dataKey="value" paddingAngle={2} strokeWidth={0}>
                        {catData.map((e, i) => <Cell key={i} fill={e.fill} opacity={hovCat && hovCat !== e.name ? 0.25 : 1} />)}
                      </Pie>
                      <Tooltip content={<PieTip />} />
                    </PieChart>
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
                      <div style={{ fontSize: hovCat ? 14 : 13, fontWeight: 800, color: hovCat ? CAT_COLORS[hovCat] || C.textPri : C.textPri, fontFamily: "'Outfit',sans-serif", lineHeight: 1.1 }}>
                        {hovCat ? (catData.find(c => c.name === hovCat)?.pct + "%") : fmt(totalExp)}
                      </div>
                      <div style={{ fontSize: 9, color: C.textMut, marginTop: 2 }}>{hovCat ? hovCat.split(" ")[0] : "total"}</div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                    {catData.map(cat => (
                      <div key={cat.name} className="cat-row"
                        onMouseEnter={() => setHovCat(cat.name)}
                        onMouseLeave={() => setHovCat(null)}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 8, cursor: "default", background: "transparent", transition: "background 0.1s", opacity: hovCat && hovCat !== cat.name ? 0.5 : 1 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: `${cat.fill}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>{CAT_ICONS[cat.name] || "💳"}</div>
                        <span style={{ fontSize: 11, fontWeight: 500, color: C.textPri, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cat.name}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: cat.fill, background: `${cat.fill}13`, padding: "1px 7px", borderRadius: 20, flexShrink: 0 }}>{cat.pct}%</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.textPri, fontFamily: "'Outfit',sans-serif", flexShrink: 0, minWidth: 68, textAlign: "right" }}>{money(cat.value, { compact: true })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </DashboardCard>

              <DashboardCard
                Icon={<LoanBalance />}
                iconColor={'bg-theme-blue-light text-theme-blue'}
                heading="Expenses by Office"
                subHeading={`${locData.length} active offices`}
                DataNotFoundText={`Expenses by Office`}
                footer={null}
                loading={isLoading}
                condition={locData.length > 1 || locData.length}
                moreOff={false}
                className={'col-span-4'}
              >
                <div className="px-7 pt-5">
                  {/* Horizontal bar chart */}
                  {console.log("locData", locData)}
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={locData} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 0 }} barSize={14}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: C.textSec, fontWeight: 500 }} axisLine={false} tickLine={false} width={68} />
                      <Tooltip content={<DarkTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="value" name="Expenses" radius={[0, 6, 6, 0]}>
                        {locData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Office cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
                    {locData.map(loc => (
                      <div key={loc.name} className="loc-card"
                        style={{ padding: "12px 13px", borderRadius: 12, background: `${loc.fill}09`, border: `1.5px solid ${loc.fill}25`, transition: "all 0.12s", cursor: "default" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 7, background: `${loc.fill}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>{LOC_EMOJI[loc.name] || "🏢"}</div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.textPri }}>{loc.name}</span>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 20, background: `${loc.fill}15`, color: loc.fill }}>{loc.pct}%</span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: loc.fill, fontFamily: "'Outfit',sans-serif", letterSpacing: -0.3 }}>{pkr(loc.value)}</div>
                        <div style={{ height: 4, background: `${loc.fill}18`, borderRadius: 3, overflow: "hidden", marginTop: 8 }}>
                          <div style={{ height: "100%", width: `${loc.pct}%`, background: loc.fill, borderRadius: 3 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DashboardCard>

              <DashboardCard
                Icon={<LineChartIcon />}
                iconColor={'bg-theme-orange-light text-theme-orange'}
                heading="Cash Flow Trends"
                subHeading={`Remittances vs expenses over time`}
                DataNotFoundText={`Cash Flow Trends`}
                footer={null}
                loading={isLoading}
                condition={true}
                moreOff={false}
                className={'col-span-12'}
                headerRight={
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {/* Legend */}
                    <div style={{ display: "flex", gap: 14 }}>
                      {[{ l: "In", c: C.green, bar: true }, { l: "Out", c: C.red, bar: true }, { l: "Net", c: C.accent, bar: false }].map(x => (
                        <div key={x.l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          {x.bar ? <div style={{ width: 10, height: 10, borderRadius: 3, background: x.c }} /> : <div style={{ width: 18, height: 2.5, background: x.c, borderRadius: 2 }} />}
                          <span style={{ fontSize: 11, color: C.textSec, fontWeight: 500 }}>{x.l === "In" ? "Remittances" : x.l === "Out" ? "Expenses" : "Net Flow"}</span>
                        </div>
                      ))}
                    </div>
                    {/* Range */}
                    <div style={{ display: "flex", borderRadius: 9, border: `1px solid ${C.border}`, overflow: "hidden", background: "#fff" }}>
                      {["3M", "6M", "12M"].map(r => (
                        <button key={r} className={`range-btn${trendRange === r ? " on" : ""}`} onClick={() => setTrendRange(r)}
                          style={{ padding: "5px 14px", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: trendRange === r ? C.accent : "#fff", color: trendRange === r ? "#fff" : C.textSec }}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                <div className="w-full px-7 pt-5">
                  <ResponsiveContainer width="100%" height={210}>
                    <ComposedChart data={trendData} margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip content={<DarkTip />} />
                      <Bar dataKey="In" name="Remittances In" fill={C.green} radius={[4, 4, 0, 0]} opacity={0.85} maxBarSize={34} />
                      <Bar dataKey="Out" name="Expenses Out" fill={C.red} radius={[4, 4, 0, 0]} opacity={0.85} maxBarSize={34} />
                      <Line type="monotone" dataKey="Net" name="Net Flow" stroke={C.accent} strokeWidth={2.5} dot={{ fill: C.accent, r: 3 }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </DashboardCard>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}